import React, { useState, useEffect, useMemo } from "react";
import { BaseReportTable, ReportFooter } from './BaseReportTable';
import { BaseColumn } from "@/components/common/table/BaseTable";
import { BaseTableCell } from "@/components/common/table/BaseTableCell";
import { useTableColumns } from "@/hooks/useTableColumns";
import { reportsApi, ReportParams } from "@/api/endpoints/reports";

interface ClientsReportProps {
  dateFilter: string;
  customDateRange: { from: string; to: string };
}

export const ClientsReport: React.FC<ClientsReportProps> = ({
  dateFilter,
  customDateRange,
}) => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { columns: baseColumns } = useTableColumns("tasks");

  useEffect(() => {
    fetchReport();
  }, [dateFilter, customDateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: ReportParams = {
        dateType: dateFilter as any,
      };
      if (dateFilter === "custom" && customDateRange.from && customDateRange.to) {
        params.fromDate = customDateRange.from;
        params.toDate = customDateRange.to;
      }

      const response = await reportsApi.getClientsReport(params);
      setReportData(response.data.data.clients || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return reportData;
    return reportData.filter((client) =>
      client.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [reportData, searchTerm]);

  // Mock functions for BaseTableCell compatibility
  const openModal = () => {};
  const handleExistingTaskChange = () => {};

  // Define columns with proper alignment
  const columns: BaseColumn[] = useMemo(() => [
    {
      key: "clientName",
      label: "CLIENT",
      sortable: true,
      visible: true,
      width: "200px",
      minWidth: "200px",
      render: (client, index) => (
        <BaseTableCell
          task={{ task: client.clientName }}
          column={{ key: "task", label: "CLIENT" }}
          index={index}
          isNewTask={false}
          isEditing={false}
          onEditStart={() => {}}
          onEditEnd={() => {}}
          onExistingTaskChange={handleExistingTaskChange}
          clientsList={[]}
          users={[]}
          openModal={openModal}
          totalTasks={filteredData.length}
          serialStart={0}
        />
      ),
    },
    {
      key: "totalTasks",
      label: "TOTAL TASKS",
      sortable: true,
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (client) => client.totalTasks || 0,
    },
    {
      key: "totalSubTasks",
      label: "TOTAL SUB-TASKS",
      sortable: true,
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (client) => client.totalSubTasks || 0,
    },
    {
      key: "totalEstimatedTime",
      label: "TOTAL EST. TIME",
      sortable: true,
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (client) => client.totalEstimatedTime || 0,
    },
    {
      key: "totalActualTime",
      label: "TOTAL TIME TAKEN",
      sortable: true,
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (client) => client.totalActualTime || 0,
    },
  ], [filteredData.length]);

  // Calculate totals for footer
  const footerTotals = useMemo(() => ({
    count: filteredData.length,
    totalTasks: filteredData.reduce((sum, client) => sum + (client.totalTasks || 0), 0),
    totalSubTasks: filteredData.reduce((sum, client) => sum + (client.totalSubTasks || 0), 0),
    totalEstimatedTime: filteredData.reduce((sum, client) => sum + (client.totalEstimatedTime || 0), 0),
    totalActualTime: filteredData.reduce((sum, client) => sum + (client.totalActualTime || 0), 0),
  }), [filteredData]);

  // Column config for footer alignment
  const columnConfig = useMemo(() => 
    columns.map(col => ({
      key: col.key,
      width: col.width || '100px',
      minWidth: col.minWidth || '100px',
      label: col.label
    }))
  , [columns]);

  return (
    <div className="h-full bg-white flex flex-col">
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full">
          <BaseReportTable
            data={filteredData}
            columns={columns}
            loading={loading}
            emptyMessage="No client data found"
            selectable={false}
            showFilters={false}
            sortable={true}
            onSort={(columnKey) => console.log("Sort by:", columnKey)}
            dropdownConfig={{}}
            selectedFilters={{}}
            getItemId={(client) =>
              client._id || client.clientId || `client-${Math.random()}`
            }
            containerClassName="h-full"
            rowClassName="border-b border-gray-100 hover:bg-gray-50"
          />
        </div>
      </div>

      <ReportFooter 
        data={filteredData}
        columnConfig={columnConfig}
        totals={footerTotals}
      />
    </div>
  );
};